import React from 'react'
import PropTypes from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'
import Tooltip from 'Pods/Shared/Tooltip'
import Loader from 'Pods/Shared/Loader'

import ProductAnalyticsTableRow from './ProductAnalyticsTableRow'
import ProductAnalyticsSortableTh from './ProductAnalyticsSortableTh'
import priceTooltipContent from './Tooltips/priceTooltipContent'

class ProductAnalyticsTable extends React.Component {
  constructor() {
    super()

    this.state = {
      isFittingContainer: false,
    }

    this.tableWrapper = React.createRef()
    this.stickyHeader = React.createRef()
    this.stickyBody = React.createRef()
    this.stickyColumn = React.createRef()
    this.actualTableBody = React.createRef()

    this.sortableThDirection = this.sortableThDirection.bind(this)
    this.handleSortByChange = this.handleSortByChange.bind(this)
    this.handleHeaderPosition = this.handleHeaderPosition.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize)

    if ((this.stickyBody.current) && (this.props.keywords.tableOffset)) {
      this.stickyBody.current.scrollLeft = this.props.keywords.tableOffset
    }

    this.handleResize()
  }

  componentDidUpdate() {
    if ((this.stickyBody.current) && (this.props.keywords.tableOffset)) {
      this.stickyBody.current.scrollLeft = this.props.keywords.tableOffset
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleHeaderPosition() {
    if (this.stickyHeader.current && this.stickyBody.current && this.stickyColumn.current) {
      this.stickyHeader.current.style.left = `${this.stickyBody.current.scrollLeft * -1}px`
      this.stickyColumn.current.style.top = `${this.stickyBody.current.scrollTop * -1}px`
    }
  }

  handleResize() {
    if ((this.tableWrapper.current) && (this.actualTableBody.current)) {
      if (this.tableWrapper.current.clientWidth < this.actualTableBody.current.clientWidth) {
        this.setState({ isFittingContainer: false })
      } else {
        this.setState({ isFittingContainer: true })
      }
    }

    this.handleHeaderPosition()
  }

  sortableThDirection(attribute) {
    return this.props.keywords.meta.searchParams.sort_by[0] == attribute
      && this.props.keywords.meta.searchParams.sort_by[1]
  }

  handleSortByChange(attribute, direction) {
    this.props.fetch({
      keywords: this.props.searchTerm,
      sort_by: [attribute, direction],
      keywords_count: this.props.scope === 'top' ? 1 : this.props.activeSearchGroup,
      per_page: this.props.tiersData.data.user.currentTier === 1 ? 10 : 100,
      group: this.props.keywords.meta.searchParams.group,
    }, this.props.scope, this.stickyBody.current.scrollLeft)
  }

  renderTable() {
    const valuesExplained = 'Values [0..100] in this column are absolute and can be compared across all reports, while'
      + ' bars indicate volumes only for the given report of 100 keywords.'

    const tableHeaders = [{
      title: 'Models',
      key: 'models_count',
      tooltip: 'Model count indicates competition: more models means you compete with more sellers for the\n'
             + 'same keyword. Click on the value to see the distribution of model Quality.',
    }, {
      title: 'Average Price',
      key: 'models_sale_price',
      tooltip: priceTooltipContent,
    }, {
      title: 'Sales',
      key: 'models_sales_count',
      tooltip: `Sales show the volume of transactions for models with the certain keyword. <br/><br/>${valuesExplained}`,
    }, {
      title: 'Searches',
      key: 'searches_count',
      tooltip: `Searches show the volume of search queries for the certain keyword. <br/><br/>${valuesExplained}`,
    }, {
      title: 'Liquidity',
      key: 'liquidity',
      tooltip: `${'Liquidity is the ratio of demand and supply (sales / number of models). It indicates which categories '
               + 'have relatively high demand compared to the competition. <br/><br/>'}${valuesExplained}`,
    }, {
      title: 'Score',
      key: 'recommendation',
      tooltip: 'Score is calculated from search volume, sales volume, competition and average price to show which '
               + 'keywords have the best value for effort. <br/><br/>'
               + 'Score values [0..100] and bars are <strong>relative</strong>, they indicate volumes '
               + '<strong>only for the given report</strong> of 100 keywords. Score values should not be compared '
               + 'between different reports.',
    }]

    const rows = []
    const maxValues = this.props.keywords.meta.maxValues

    this.props.keywords.data.forEach((keyword, i) => {
      if (this.props.keywords.hidden && i > (this.props.modalIsOpen ? 99 : this.props.keywords.show - 1)) {
        return
      }

      rows.push(
        <ProductAnalyticsTableRow
          key={keyword.id}
          keyword={keyword}
          maxValues={maxValues}
          searchKeyword={this.props.searchKeyword}
          searchTerm={this.props.searchTerm}
        />
      )
    })

    return (
      <div className="table-responsive table-responsive--default table-responsive--sticky-top u-mb0">
        <table ref={this.stickyHeader} className={`table-responsive__sticky-header analytics-table analytics-table--marketplace-statistics ${!this.state.isFittingContainer ? '' : 'analytics-table__header-fits'}`}>
          <thead>
            <tr className="table-row--1x">
              <th>
                <div className="responsive-table__content">
                  Keyword
                  <Tooltip
                    tooltipClassName="tooltip--medium tooltip--spaced"
                    id="table-keyword--tooltip"
                    content="Use shortcuts for Google Images and CGTrader search results to get more ideas about each
                    keyword. Press on the keyword to use it in the Keyword Search section below."
                  >
                    <Icon type={['fal', 'question-circle']} color="grey" className="info-icon" notSpaced />
                  </Tooltip>
                </div>
              </th>
              <th />

              {tableHeaders.map((item) => {
                return (
                  <ProductAnalyticsSortableTh
                    attribute={item.key}
                    direction={this.sortableThDirection(item.key)}
                    onSortByChange={this.handleSortByChange}
                    defaultSortingDesc
                    key={item.key}
                  >
                    {item.title}
                    <Tooltip
                      place="bottom"
                      tooltipClassName="tooltip--medium tooltip--spaced"
                      id={`table-${item.title}--tooltip`}
                      content={item.tooltip}
                      html
                    >
                      <Icon type={['fal', 'question-circle']} color="grey" className="info-icon" notSpaced />
                    </Tooltip>
                  </ProductAnalyticsSortableTh>
                )
              })}
            </tr>
          </thead>
        </table>

        <div className={`table-responsive__table-body ${!this.state.isFittingContainer ? 'table-responsive--sticky-left' : ''}`} ref={this.stickyBody} onScroll={this.handleHeaderPosition}>
          {!this.state.isFittingContainer && (
            <div className="analytics-table-wrapper">
              <div className="table-responsive--sticky-left table-responsive__clone" ref={this.stickyColumn}>
                <table className={`analytics-table analytics-table--marketplace-statistics ${this.props.head} ${this.props.type}`}>
                  <tbody>
                    {rows}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <table ref={this.actualTableBody} className={`analytics-table analytics-table--marketplace-statistics ${this.props.head} ${this.props.type}`}>
            <tbody>
              {rows}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div ref={this.tableWrapper} className="table-responsive__wrapper">
        {!this.props.keywords.isSorting ? (
          this.renderTable()
        ) : (
          <div className="card card--flat">
            <Loader />
          </div>
        )}
      </div>
    )
  }
}

ProductAnalyticsTable.propTypes = {
  type: PropTypes.string,
  keywords: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      keywords: PropTypes.string.isRequired,
      liquidity: PropTypes.number.isRequired,
      modelsListingPrice: PropTypes.number.isRequired,
      modelsSalePrice: PropTypes.number.isRequired,
      modelsCount: PropTypes.number.isRequired,
      recommendation: PropTypes.number.isRequired,
      modelsSalesCount: PropTypes.number.isRequired,
      searchesCount: PropTypes.number.isRequired,
      isSearchable: PropTypes.bool.isRequired,
    })).isRequired,
    meta: PropTypes.shape({
      maxValues: PropTypes.shape({
        liquidity: PropTypes.number.isRequired,
        modelsListingPrice: PropTypes.number.isRequired,
        modelsSalePrice: PropTypes.number.isRequired,
        modelsCount: PropTypes.number.isRequired,
        recommendation: PropTypes.number.isRequired,
        modelsSalesCount: PropTypes.number.isRequired,
        searchesCount: PropTypes.number.isRequired,
      }),
    }).isRequired,
    hidden: PropTypes.bool.isRequired,
    show: PropTypes.number,
    tableOffset: PropTypes.number,
    isSorting: PropTypes.bool,
  }).isRequired,
  searchKeyword: PropTypes.func,
  fetch: PropTypes.func.isRequired,
  scope: PropTypes.string.isRequired,
  modalIsOpen: PropTypes.bool,
  tiersData: PropTypes.object.isRequired,
}

ProductAnalyticsTable.defaultPropTypes = {
  type: 'simple',
  keywords: {
    show: 10,
    tableOffset: 0,
    isSorting: false,
  },
  searchKeyword: null,
  modalIsOpen: false,
}

export default ProductAnalyticsTable
